import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { InventoryCategorySummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

/**
 * The category tree items are classified under — Audio, Mobiliario, Cocina...
 * Flat, not a nested tree view: a church's catalog rarely runs more than two
 * levels deep, and the parent name in its own column already shows the
 * relationship without the complexity of a collapsible tree widget.
 */
export function CategoriesListPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isCreating, setIsCreating] = useState(false);

  const query = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: ({ signal }) => inventoryApi.listCategories({ limit: 100 }, signal),
  });

  const categories = query.data?.data ?? [];

  return (
    <div className="page">
      <Link to="/inventory" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <h1 className="page-title">{t('inventory.category.title')}</h1>
        {can('inventory.category.create') && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            onClick={() => setIsCreating(true)}
          >
            {t('inventory.category.create')}
          </button>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && categories.length === 0 && (
        <EmptyState title={t('inventory.category.empty')} hint={t('inventory.category.create')} />
      )}

      {categories.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('inventory.category.singular')}</th>
                <th scope="col">{t('inventory.category.parentCategory')}</th>
                <th scope="col">{t('inventory.item.title')}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <CategoryRow key={category.id} category={category} all={categories} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <CategoryForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function CategoryRow({
  category,
  all,
}: {
  category: InventoryCategorySummaryResponse;
  all: readonly InventoryCategorySummaryResponse[];
}) {
  const { t } = useI18n();
  const parent = all.find((candidate) => candidate.id === category.parentCategoryId);

  return (
    <tr data-archived={category.archivedAt ? true : undefined}>
      <td>{category.name}</td>
      <td>{parent ? parent.name : <span className="cell-empty">—</span>}</td>
      <td dir="ltr">{t('inventory.item.count', { count: category.itemCount })}</td>
    </tr>
  );
}

function CategoryForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const categories = useQuery({
    queryKey: ['inventory', 'categories', 'options'],
    queryFn: ({ signal }) => inventoryApi.listCategories({ limit: 100 }, signal),
  });

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.createCategory({
        name: name.trim(),
        parentCategoryId: parentCategoryId || null,
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="category-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="category-form-title">{t('inventory.category.create')}</h2>
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('inventory.category.name')}
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />

        <div className="field">
          <label className="field-label" htmlFor="field-parentCategoryId">
            {t('inventory.category.parentCategory')}
          </label>
          <select
            id="field-parentCategoryId"
            className="field-input"
            value={parentCategoryId}
            onChange={(e) => setParentCategoryId(e.target.value)}
          >
            <option value="">—</option>
            {(categories.data?.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            disabled={!name.trim()}
          >
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
